import {createStyles} from '@mantine/core';
import React, {useState} from 'react';

import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';

import {SKILL_MD_PATH, parseSkillFrontmatter} from '../tool-details/ManageSkillToolCallParser';

const MIN_EDITOR_HEIGHT = 120;
const MAX_EDITOR_HEIGHT = 320;
const BORDER_OFFSET = 2;

const useContentStyles = createStyles((theme) => ({
  codeBlock: {
    ...FONT_PROXIMA_12,
    color: theme.colors.gray[7],
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    whiteSpace: 'pre-wrap',
    fontFamily: theme.fontFamilyMonospace,
    lineHeight: 1.4,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  editorContainer: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  markdownContainer: {
    ...FONT_PROXIMA_14,
    padding: `0 ${theme.spacing.md}`,
    backgroundColor: theme.white,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  skillMdBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
    padding: theme.spacing.md,
  },
  frontmatterBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    paddingBottom: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  frontmatterField: {
    ...FONT_PROXIMA_14,
    color: theme.colors.gray[7],
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'baseline',
  },
  frontmatterLabel: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    flexShrink: 0,
  },
  frontmatterValue: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
}));

export function MonoBlock({text}: {text: string}): JSX.Element {
  const {classes} = useContentStyles();
  return <div className={classes.codeBlock}>{text}</div>;
}

export function ReadOnlyCodeBlock({code, language}: {code: string; language: MonacoLanguage}): JSX.Element {
  const {classes} = useContentStyles();
  const [height, setHeight] = useState(MIN_EDITOR_HEIGHT);

  return (
    <div className={classes.editorContainer} style={{height}}>
      <MonacoEditor
        value={code}
        language={language}
        height="100%"
        width="100%"
        theme="vs"
        options={{readOnly: true, minimap: {enabled: false}, scrollBeyondLastLine: false, wordWrap: 'on'}}
        onChangeContentSize={(e) => {
          setHeight(
            Math.min(Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET), MAX_EDITOR_HEIGHT)
          );
        }}
      />
    </div>
  );
}

export function MarkdownBlock({markdown}: {markdown: string}): JSX.Element {
  const {classes} = useContentStyles();
  return (
    <div className={classes.markdownContainer}>
      <LLMMarkdownSection markdown={markdown} />
    </div>
  );
}

function SkillMdBlock({content}: {content: string}): JSX.Element {
  const {classes} = useContentStyles();
  const {name, description, body} = parseSkillFrontmatter(content);
  if (name == null && description == null) {
    return <MarkdownBlock markdown={body} />;
  }
  return (
    <div className={classes.skillMdBlock}>
      <div className={classes.frontmatterBlock}>
        {name != null && (
          <div className={classes.frontmatterField}>
            <span className={classes.frontmatterLabel}>Name:</span>
            <span className={classes.frontmatterValue}>{name}</span>
          </div>
        )}
        {description != null && (
          <div className={classes.frontmatterField}>
            <span className={classes.frontmatterLabel}>Description:</span>
            <span className={classes.frontmatterValue}>{description}</span>
          </div>
        )}
      </div>
      {body.trim().length > 0 && <LLMMarkdownSection markdown={body} />}
    </div>
  );
}

type SupportedLanguage = 'python' | 'sql' | 'json';

interface FileExtensionConfig {
  extension: string;
  language: SupportedLanguage;
}

const CODE_FILE_EXTENSIONS: Array<FileExtensionConfig> = [
  {extension: '.py', language: 'python'},
  {extension: '.sql', language: 'sql'},
  {extension: '.json', language: 'json'},
];

export interface SkillFileContentRendererProps {
  virtualPath: string;
  content: string;
}

export function SkillFileContentRenderer({virtualPath, content}: SkillFileContentRendererProps): JSX.Element {
  const lowerPath = virtualPath.toLowerCase();

  if (virtualPath === SKILL_MD_PATH) {
    return <SkillMdBlock content={content} />;
  }

  if (lowerPath.endsWith('.md')) {
    return <MarkdownBlock markdown={content} />;
  }

  for (const {extension, language} of CODE_FILE_EXTENSIONS) {
    if (lowerPath.endsWith(extension)) {
      return <ReadOnlyCodeBlock code={content} language={language} />;
    }
  }

  return <MonoBlock text={content} />;
}
